import { useCallback, useEffect, useRef, useState } from "react"
import NiceModal from "@ebay/nice-modal-react"
import Box from "@mui/material/Box"
import Button from "@mui/material/Button"
import { enqueueSnackbar } from "notistack"
import ISO6391 from "iso-639-1"

import { useReduxState } from "../hooks/use_redux_state"
import { useAppSelector } from "@/lib/hooks"
import type { SxStyles } from "@/types/util"
import useTranslation from "../hooks/use_translation"
import LanguageSwitcherModal from "./language_switcher_modal"

const styles = {
  container: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
  },
  pickerContainer: {
    height: "150px",
    width: "100%",
    overflowY: "scroll",
  },
  pickerItem: {
    height: "40px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "18px",
    color: "#555",
    cursor: "pointer",
    transition: "box-shadow ease .2s",
  },
  selectedItem: {
    color: "#000",
    boxShadow: "inset 0 1px 0 lightgray, inset 0 -1px 0 lightgray",
  },
} satisfies SxStyles

type LanguageOption = {
  id: string
  label: string
}

const LanguagesPicker = () => {
  const languages = useAppSelector((state) => state.configuration?.languages) ?? []
  const [currentLanguageCode, setCurrentLanguageCode] = useReduxState({
    key: "currentLanguageCode",
  })
  const { t } = useTranslation()

  const options: LanguageOption[] = languages.map((lng) => ({
    id: ISO6391.getCode(lng),
    label: t(lng.toLowerCase()),
  }))

  let defaultOptionSelected: string | null
  if (options.length === 0) {
    defaultOptionSelected = null
  } else if (currentLanguageCode) {
    defaultOptionSelected = currentLanguageCode
  } else {
    defaultOptionSelected = options[0]?.id ?? null
  }

  const [selectedOptionId, setSelectedOptionId] = useState<string | null>(defaultOptionSelected)
  const containerRef = useRef<HTMLDivElement | null>(null)
  const itemHeight = 40

  const scrollToOption = useCallback(
    (optionId: string) => {
      const index = options.findIndex((option) => option.id === optionId)
      containerRef.current?.scrollTo({
        top: index * itemHeight,
        behavior: "smooth",
      })
    },
    [options, itemHeight],
  )

  const handleScroll = () => {
    if (!containerRef.current) return
    const index = Math.round(containerRef.current.scrollTop / itemHeight)
    const selectedIndex = options.findIndex((option) => option.id === selectedOptionId)
    const next = options[index]
    if (next && index !== selectedIndex) {
      setSelectedOptionId(next.id)
    }
  }

  const handleClick = (id: string) => {
    scrollToOption(id)
    setSelectedOptionId(id)
  }

  useEffect(() => {
    if (selectedOptionId) scrollToOption(selectedOptionId)
  }, [scrollToOption, selectedOptionId])

  const onSubmit = (selectedId: string | null) => {
    if (!selectedId) return
    setCurrentLanguageCode(selectedId)
    setTimeout(() => {
      enqueueSnackbar(t("languageHasBeenChanged"), { variant: "success" })
    }, 700)
    void NiceModal.hide(LanguageSwitcherModal)
  }

  return (
    <div>
      <Box sx={styles.container}>
        <Box ref={containerRef} onScroll={handleScroll} sx={styles.pickerContainer}>
          {options.map((option) => (
            <Box
              key={option.id}
              onClick={() => handleClick(option.id)}
              sx={{
                ...styles.pickerItem,
                ...(option.id === selectedOptionId ? styles.selectedItem : {}),
              }}
            >
              {option.label}
            </Box>
          ))}
        </Box>
      </Box>
      <Button
        onClick={() => {
          onSubmit(selectedOptionId)
        }}
        variant="contained"
        color="primary"
      >
        {t("save")}
      </Button>
    </div>
  )
}

export default LanguagesPicker
